import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { CSRF_COOKIE, CSRF_HEADER, type ApiError } from '@biyolink/shared';
import { t } from '@/i18n';

/**
 * Ortak axios örneği.
 * Geliştirmede Vite proxy'si `/api` isteklerini 4000 portuna taşır → aynı origin,
 * httpOnly cookie'ler (at / rt) sorunsuz gider.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env['VITE_API_BASE_URL'] ?? '/api/v1',
  withCredentials: true,
  timeout: 20_000,
  // Dizi parametreler `oz=a&oz=b` olarak gitmeli. Varsayılan biçim `oz[]=a` üretiyor,
  // Express bunu `oz[]` adlı ayrı bir anahtar olarak okuyor ve kriter filtresi kayboluyor.
  paramsSerializer: { indexes: null },
});

/** Sunucudan gelen Türkçe hata mesajını taşıyan hata sınıfı. */
export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: { path: string; message: string }[];

  constructor(
    code: string,
    status: number,
    message: string,
    details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Form alanı adına göre doğrulama mesajı döndürür. */
  detailFor(path: string): string | undefined {
    return this.details?.find((detail) => detail.path === path)?.message;
  }
}

function readCookie(name: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.slice(name.length + 1);
}

/**
 * Ortada bir oturum olabileceğinin işareti. CSRF cookie'si yalnızca oturum açılırken
 * (giriş/kayıt/yenileme) yazıldığı ve JS'ten okunabildiği için bu bilgiyi ücretsiz veriyor;
 * yoksa `/auth/me` sorgusu boşuna 401 üretir — her ziyaretçinin konsolunda hata görünür.
 */
export function hasSessionCookie(): boolean {
  return readCookie(CSRF_COOKIE) !== undefined;
}

const SAFE_METHODS = new Set(['get', 'head', 'options']);

/** CSRF double-submit: cookie'deki değeri başlıkta geri gönderiyoruz. */
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const method = (config.method ?? 'get').toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE);
    if (token) config.headers.set(CSRF_HEADER, token);
  }
  return config;
});

/** Oturum süresi dolduğunda tek seferlik yenileme denenir. */
let refreshPromise: Promise<void> | null = null;

/** 401 sonrası yenileme de başarısız olursa çağrılır (giriş sayfasına yönlendirme). */
let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

/** Yenileme denemesi yapılmayacak uçlar — sonsuz döngüyü engeller. */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/logout'];

async function refreshSession(): Promise<void> {
  refreshPromise ??= apiClient
    .post('/auth/refresh')
    .then(() => undefined)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function toApiError(error: AxiosError<ApiError>): ApiRequestError {
  if (!error.response) {
    return new ApiRequestError('NETWORK_ERROR', 0, t('errors.network'));
  }
  const payload = error.response.data?.error;
  return new ApiRequestError(
    payload?.code ?? 'UNKNOWN',
    error.response.status,
    payload?.message ?? t('errors.unexpected'),
    payload?.details,
  );
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const config = error.config as
      (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const url = config?.url ?? '';
    const shouldTryRefresh =
      error.response?.status === 401 &&
      config &&
      !config._retried &&
      // Yenilenecek bir oturum yoksa boşuna istek atmayalım.
      hasSessionCookie() &&
      !NO_REFRESH_PATHS.some((path) => url.startsWith(path));

    if (shouldTryRefresh) {
      try {
        await refreshSession();
        config._retried = true;
        return await apiClient.request(config);
      } catch {
        onSessionExpired?.();
      }
    }

    return Promise.reject(toApiError(error));
  },
);

/** `{ data }` zarfını açar. */
export async function getData<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<{ data: T }>(url, { params });
  return response.data.data;
}

/** POST/PATCH için `{ data }` zarfını açan yardımcılar. */
export async function postData<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.post<{ data: T }>(url, body);
  return response.data.data;
}

export async function patchData<T>(url: string, body?: unknown): Promise<T> {
  const response = await apiClient.patch<{ data: T }>(url, body);
  return response.data.data;
}
